import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Grid,
    Box,
    Chip,
    Divider,
    Avatar,
    IconButton
} from '@mui/material';
import { 
    Person as PersonIcon, 
    Email as EmailIcon, 
    Phone as PhoneIcon, 
    Work as WorkIcon, 
    School as SchoolIcon,
    Download as DownloadIcon,
    Close as CloseIcon
} from '@mui/icons-material';

interface JobApplicantViewProps {
    open: boolean;
    onClose: () => void;
    applicant: any;
}

const JobApplicantView: React.FC<JobApplicantViewProps> = ({ open, onClose, applicant }) => {
    if (!applicant) return null;

    const getStatusColor = (status: string) => {
        const statusConfig = {
            pending: 'warning' as const,
            shortlisted: 'info' as const,
            rejected: 'error' as const,
            hired: 'success' as const
        };
        return statusConfig[status as keyof typeof statusConfig] || 'warning';
    };

    const getStatusLabel = (status: string) => {
        const statusConfig = {
            pending: 'Pending',
            shortlisted: 'Shortlisted',
            rejected: 'Rejected',
            hired: 'Hired'
        };
        return statusConfig[status as keyof typeof statusConfig] || 'Pending';
    };

    const handleDownloadResume = (resumeUrl: string, applicantName: string) => {
        const link = document.createElement('a');
        link.href = resumeUrl;
        link.download = `${applicantName}_resume.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                borderBottom: '1px solid rgba(0, 0, 0, 0.12)'
            }}>
                <Typography variant="h6">
                    Job Applicant Details
                </Typography>
                <IconButton onClick={onClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                <Box sx={{ mt: 2 }}>
                    <Grid container spacing={3}>
                        <Grid size={{ xs: 12 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                                <Avatar sx={{ bgcolor: 'primary.main', width: 64, height: 64 }}>
                                    <PersonIcon sx={{ fontSize: 32 }} />
                                </Avatar>
                                <Box>
                                    <Typography variant="h5" component="h2" sx={{ fontWeight: 600 }}>
                                        {applicant.name}
                                    </Typography>
                                    <Typography variant="subtitle1" color="textSecondary">
                                        {applicant.designation}
                                    </Typography>
                                    <Chip 
                                        label={getStatusLabel(applicant.status)} 
                                        color={getStatusColor(applicant.status)}
                                        size="small"
                                        sx={{ mt: 1 }}
                                    />
                                </Box>
                            </Box>
                        </Grid>

                        <Grid size={{ xs: 12 }}>
                            <Divider sx={{ my: 2 }} />
                        </Grid>

                        <Grid size={{ xs: 12, sm: 6 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <EmailIcon color="action" />
                                <Typography variant="subtitle2" color="textSecondary">
                                    Email
                                </Typography>
                            </Box>
                            <Typography variant="body1" sx={{ ml: 3 }}>
                                {applicant.email}
                            </Typography>
                        </Grid>

                        <Grid size={{ xs: 12, sm: 6 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <PhoneIcon color="action" />
                                <Typography variant="subtitle2" color="textSecondary">
                                    Phone
                                </Typography>
                            </Box>
                            <Typography variant="body1" sx={{ ml: 3 }}>
                                {applicant.phone}
                            </Typography>
                        </Grid>

                        <Grid size={{ xs: 12, sm: 6 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <WorkIcon color="action" />
                                <Typography variant="subtitle2" color="textSecondary">
                                    Designation
                                </Typography>
                            </Box>
                            <Typography variant="body1" sx={{ ml: 3 }}>
                                {applicant.designation}
                            </Typography>
                        </Grid>

                        <Grid size={{ xs: 12, sm: 6 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <SchoolIcon color="action" />
                                <Typography variant="subtitle2" color="textSecondary">
                                    Education
                                </Typography>
                            </Box>
                            <Typography variant="body1" sx={{ ml: 3 }}>
                                {applicant.education}
                            </Typography>
                        </Grid>

                        <Grid size={{ xs: 12 }}>
                            <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>
                                Experience
                            </Typography>
                            <Typography variant="body1">
                                {applicant.experience}
                            </Typography>
                        </Grid>

                        {applicant.resume_url && (
                            <Grid size={{ xs: 12 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Typography variant="subtitle2" color="textSecondary">
                                        Resume:
                                    </Typography>
                                    <Button
                                        variant="outlined"
                                        startIcon={<DownloadIcon />}
                                        onClick={() => handleDownloadResume(applicant.resume_url, applicant.name)}
                                        size="small"
                                    >
                                        Download Resume
                                    </Button>
                                </Box>
                            </Grid>
                        )}

                        {applicant.created_at && (
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>
                                    Applied On
                                </Typography>
                                <Typography variant="body2">
                                    {new Date(applicant.created_at).toLocaleDateString()}
                                </Typography>
                            </Grid>
                        )}

                        {applicant.updated_at && (
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>
                                    Last Updated
                                </Typography>
                                <Typography variant="body2">
                                    {new Date(applicant.updated_at).toLocaleDateString()}
                                </Typography>
                            </Grid>
                        )}
                    </Grid>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default JobApplicantView;
